import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { FiChevronDown, FiSearch } from "react-icons/fi";
import BubbleLoader from "./BubbleLoader.jsx";
import Tooltip from "./Tooltip.jsx";
import "./Dropdown.css";

const normalizeOption = (opt) => {
  if (typeof opt === "string") return { value: opt, label: opt };
  if (opt.title != null) {
    return {
      ...opt,
      label: opt.label ?? [opt.title, opt.subtext].filter(Boolean).join(" — "),
      searchText: opt.searchText ?? [opt.title, opt.subtext].filter(Boolean).join(" "),
    };
  }
  return opt;
};

const Dropdown = ({
  value,
  onChange,
  options = [],
  width = "100%",
  placeholder = "Select",
  disabled = false,
  noDataText = "No results found",
  startAdornment = null,
  endAdornment = null,
  className = "",
  valueLabel = null,
  showValueTooltip = false,
  showOptionTooltip = false,
  onOpen,
  onClose,
  searchable = false,
  searchThreshold = 10,
  searchPlaceholder = "Search…",
  loading = false,
  loadingText = "Loading…",
  error = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [menuRect, setMenuRect] = useState(null);
  const containerRef = useRef(null);
  const menuRef = useRef(null);

  const normalizedOptions = useMemo(
    () => Array.from(new Map(options.map((opt) => [normalizeOption(opt).value, normalizeOption(opt)])).values()),
    [options]
  );

  const selectedOption = normalizedOptions.find((o) => o.value === value);
  // An empty/nullish value always renders the placeholder, even if some
  // option happens to carry that same value (e.g. an "All" clear option).
  const hasSelectedValue = value !== '' && value != null && selectedOption !== undefined;

  const triggerLabel = valueLabel != null ? valueLabel : selectedOption?.label;

  const showSearch = searchable && normalizedOptions.length >= searchThreshold;

  const visibleOptions = useMemo(() => {
    if (!showSearch || !search.trim()) return normalizedOptions;
    const q = search.trim().toLowerCase();
    return normalizedOptions.filter((opt) =>
      (opt.searchText || opt.label || "").toLowerCase().includes(q)
    );
  }, [normalizedOptions, showSearch, search]);

  // The menu is portaled to <body> so it can float above any scrollable
  // ancestor (e.g. a modal) without being clipped or counted into that
  // ancestor's own scrollable area. Position is measured from the trigger,
  // flipping above it when there isn't enough room below within the viewport
  // — otherwise short viewports (or a trigger near the bottom of the page)
  // push the menu's own options off-screen. Height stays the static CSS
  // max-height (see .customer-portal-dropdown__menu) in the common case —
  // STATIC_MAX_HEIGHT here only informs the flip decision, kept in sync with
  // that CSS value. Only when there genuinely isn't STATIC_MAX_HEIGHT worth of
  // room in the chosen direction (e.g. a trigger inside a short modal, with
  // little space either above or below it) do we shrink the menu to the
  // available space via an inline maxHeight, so it stays fully reachable
  // instead of being clipped off-screen.
  useLayoutEffect(() => {
    if (!open) return;
    const GAP = 4;
    const STATIC_MAX_HEIGHT = 280;
    const MIN_MAX_HEIGHT = 120;
    const updateRect = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - GAP;
      const spaceAbove = rect.top - GAP;
      const openUpward = spaceBelow < STATIC_MAX_HEIGHT && spaceAbove > spaceBelow;
      const availableSpace = openUpward ? spaceAbove : spaceBelow;
      const maxHeight = Math.max(MIN_MAX_HEIGHT, Math.min(STATIC_MAX_HEIGHT, availableSpace));
      setMenuRect({
        left: rect.left,
        width: rect.width,
        top: openUpward ? null : rect.bottom + GAP,
        bottom: openUpward ? window.innerHeight - rect.top + GAP : null,
        openUpward,
        maxHeight,
      });
    };
    updateRect();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    // `target` is not always a Node — a window "resize" event fires with
    // target === window, and Node.contains() throws on non-Node input.
    const isInside = (target) =>
      target instanceof Node &&
      (containerRef.current?.contains(target) || menuRef.current?.contains(target));

    const handleClickOutside = (e) => {
      if (isInside(e.target)) return;
      setOpen(false);
      onClose?.();
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        onClose?.();
      }
    };
    // Any ancestor scrolling (e.g. a modal body) would leave the portaled
    // menu misaligned with its trigger, so just close it — scrolling the
    // menu's own option list is excluded via the isInside check.
    const handleScrollOrResize = (e) => {
      if (isInside(e.target)) return;
      setOpen(false);
      onClose?.();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const toggle = () => {
    if (disabled) return;
    const next = !open;
    setOpen(next);
    next ? onOpen?.() : onClose?.();
  };

  const handleOptionClick = (opt) => {
    if (opt.disabled) return;
    onChange?.(opt.value);
    setOpen(false);
  };

  return (
    <div
      className={`customer-portal-dropdown ${className}`.trim()}
      style={{ width }}
      ref={containerRef}
    >
      <button
        type="button"
        className={`customer-portal-dropdown__trigger ${disabled ? "is-disabled" : ""} ${error ? "is-error" : ""}`}
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled}
      >
        {startAdornment ? (
          <span className="customer-portal-dropdown__start-adornment">{startAdornment}</span>
        ) : null}
        <div className="customer-portal-dropdown__trigger-content">
          {hasSelectedValue ? (
            showValueTooltip ? (
              <Tooltip as="span" className="customer-portal-dropdown__label">{triggerLabel}</Tooltip>
            ) : (
              <span className="customer-portal-dropdown__label">{triggerLabel}</span>
            )
          ) : (
            <span className="customer-portal-dropdown__placeholder">{placeholder}</span>
          )}
        </div>
        <span className="customer-portal-dropdown__trigger-actions">
          {endAdornment ? (
            <span
              className="customer-portal-dropdown__adornment"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => e.stopPropagation()}
            >
              {endAdornment}
            </span>
          ) : null}
          <span
            className={`customer-portal-dropdown__arrow ${open ? "is-open" : ""} ${
              open && menuRect?.openUpward ? "is-open-upward" : ""
            }`}
          >
            <FiChevronDown />
          </span>
        </span>
      </button>

      {open && !disabled && menuRect && createPortal(
        <div
          ref={menuRef}
          className="customer-portal-dropdown__menu customer-portal-dropdown__menu--portal"
          role="listbox"
          style={{
            position: "fixed",
            // Explicit "auto" on whichever side isn't in use — otherwise the
            // CSS class's own `top: calc(100% + 4px)` rule leaks through
            // whenever this inline style omits `top` (the upward-flip case),
            // fighting the inline `bottom` and collapsing the menu's height.
            top: menuRect.top != null ? menuRect.top : "auto",
            bottom: menuRect.bottom != null ? menuRect.bottom : "auto",
            left: menuRect.left,
            width: menuRect.width,
            maxHeight: menuRect.maxHeight,
          }}
        >
          {showSearch && (
            <div className="customer-portal-dropdown__search">
              <div className="customer-portal-dropdown__search-box">
                <FiSearch />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}
          {loading ? (
            <div className="customer-portal-dropdown__loading" aria-label={loadingText}>
              <BubbleLoader size={0.45} />
            </div>
          ) : visibleOptions.length > 0 ? (
            visibleOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`customer-portal-dropdown__option ${opt.title != null ? "customer-portal-dropdown__option--two-line" : ""} ${opt.value === value ? "is-selected" : ""} ${opt.disabled ? "is-disabled" : ""}`}
                disabled={opt.disabled}
                onClick={() => handleOptionClick(opt)}
                role="option"
                aria-selected={opt.value === value}
              >
                {opt.title != null ? (
                  <span className="customer-portal-dropdown__option-text">
                    <span className="customer-portal-dropdown__option-title">{opt.title}</span>
                    {opt.subtext && (
                      <span className="customer-portal-dropdown__option-subtext">{opt.subtext}</span>
                    )}
                  </span>
                ) : showOptionTooltip ? (
                  <Tooltip as="span" className="customer-portal-dropdown__label">{opt.label}</Tooltip>
                ) : (
                  <span className="customer-portal-dropdown__label">{opt.label}</span>
                )}
              </button>
            ))
          ) : (
            <div className="customer-portal-dropdown__no-data">{noDataText}</div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default Dropdown;
